"use client";

import React, { useEffect, useRef } from "react";
import "./customer-revenue.css";

const customerRevenueImages = {
  hero: "/assets/images/Revenue-is-not.jpg",
  dimensions: "/assets/images/see-revenue.jpg",
  insightToAction: "/assets/images/from-insight.png",
  cta: "/assets/images/cta-img.jpg",
};

const questions = [
  "Which customers generate revenue but destroy margin?",
  "Which customers are becoming less valuable?",
  "Which customers may reduce or leave?",
  "Where are discounts and schemes leaking margin?",
  "Which receivables are likely to become overdue?",
  "Where is our best revenue-growth opportunity?",
  "Which SKUs are growing but diluting contribution?",
  "Is growth coming from price, volume or mix?",
  "Which channels are actually profitable?",
  "Where are we selling more but earning less?",
];

const dimensions = [
  "Customer",
  "Product / SKU",
  "Brand / Category",
  "Geography",
  "Channel",
  "Distributor / Partner",
  "Salesperson",
  "Price",
  "Scheme / Promotion",
  "Time",
];

const intelligenceAreas = [
  ["Customer Profitability", "Revenue · Margin · Cost-to-Serve · Cash", "customer"],
  ["Pricing Intelligence", "List Price · Realisation · Discount · Variance", "pricing"],
  ["Product & SKU Performance", "Volume · Mix · Growth · Contribution", "product"],
  ["Scheme & Promotion Intelligence", "Scheme · Spend · Lift · Leakage · ROI", "promotion"],
  ["Receivables Intelligence", "Invoice · Ageing · Collection · Risk · Cash", "receivables"],
  ["Growth Intelligence", "Customer · Product · Geography · Channel · Opportunity", "growth"],
];

const actionSteps = [
  ["Who changed?", "Customer, product, channel or geography"],
  ["What changed?", "Volume, price, discount, mix, cost, cash"],
  ["Why?", "Find the real drivers"],
  ["Structural or temporary?", "Trend, seasonality or one-off"],
  ["What happens next?", "Forecast and scenarios"],
  ["What can we change?", "Price · Mix · Scheme · Credit · Service"],
  ["Measure value", "Track impact and sustain improvement"],
];

const businessProblems = [
  "Which customers generate revenue but destroy margin?",
  "Which customers are becoming less valuable?",
  "Which customers may reduce or leave?",
  "Where are discounts and schemes leaking margin?",
  "Which receivables are likely to become overdue?",
  "Where is our best revenue-growth opportunity?",
];

const connectedQuestions = [
  "Why did margin fall?",
  "Why is revenue growing but profit isn’t?",
  "Which products are profitable?",
  "Where are costs growing faster?",
  "What will cash look like in 30 / 60 / 90 days?",
  "Why are actuals deviating from plan?",
];

const systems = [
  ["sap", "ERP / S/4HANA"],
  ["crm", "CRM"],
  ["dms", "DMS"],
  ["pos", "POS"],
  ["pricing", "Pricing"],
  ["promotions", "Promotions"],
  ["invoices", "Invoices"],
  ["receivables", "Receivables"],
  ["external", "External Data"],
];

const dataContext = [
  ["CUSTOMER MASTER", "Customer hierarchy · Segments · Ownership"],
  ["PRODUCT HIERARCHY", "Product · SKU · Brand · Category"],
  ["COMMERCIAL DATA", "Price · Discount · Scheme · Channel"],
  ["FINANCIAL DATA", "Revenue · Cost · Margin · Receivables"],
];

/* ---------- Scroll reveal (bottom to top) ---------- */

function useScrollReveal() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const items = root.querySelectorAll<HTMLElement>("[data-reveal]");

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );

    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return ref;
}

const delay = (i: number, step = 80, max = 400) =>
  ({ "--cr-delay": `${Math.min(i * step, max)}ms` } as React.CSSProperties);

/* ---------- Components ---------- */

type HeadingProps = {
  eyebrow: string;
  title: string;
  description: string;
};

function Heading({ eyebrow, title, description }: HeadingProps) {
  return (
    <header className="cr-heading" data-reveal>
      <span className="cr-eyebrow">{eyebrow}</span>
      <h2>{title}</h2>
      <p>{description}</p>
    </header>
  );
}

type SectionProps = {
  className?: string;
  id?: string;
  children: React.ReactNode;
};

function Section({ className = "", id, children }: SectionProps) {
  return (
    <section id={id} className={`cr-section ${className}`.trim()}>
      <div className="cr-wrap">{children}</div>
    </section>
  );
}

type IconName =
  | "customer"
  | "pricing"
  | "product"
  | "promotion"
  | "receivables"
  | "growth";

function IconMark({ name }: { name: IconName }) {
  const icons: Record<IconName, React.ReactNode> = {
    customer: (
      <>
        <circle cx="12" cy="8" r="3.2" />
        <path d="M5.5 20v-1.4a6.5 6.5 0 0 1 13 0V20" />
        <path d="M4 20h16" />
      </>
    ),
    pricing: (
      <>
        <path d="M20.2 13.2 13 20.4a1.8 1.8 0 0 1-2.5 0L3.6 13.5a1.8 1.8 0 0 1 0-2.5l7.2-7.2h6.4l3 3v6.4Z" />
        <circle cx="15.3" cy="8.7" r="1.2" />
      </>
    ),
    product: (
      <>
        <rect x="3.5" y="4" width="7" height="7" rx="1" />
        <rect x="13.5" y="4" width="7" height="7" rx="1" />
        <rect x="3.5" y="14" width="7" height="7" rx="1" />
        <rect x="13.5" y="14" width="7" height="7" rx="1" />
      </>
    ),
    promotion: (
      <>
        <path d="M20 12v8H4v-8" />
        <path d="M2.5 7.5h19v4.5h-19z" />
        <path d="M12 7.5V20" />
        <path d="M12 7.5H8.4a2.2 2.2 0 1 1 2.2-2.2c0 1.2 1.4 2.2 1.4 2.2Z" />
        <path d="M12 7.5h3.6a2.2 2.2 0 1 0-2.2-2.2c0 1.2-1.4 2.2-1.4 2.2Z" />
      </>
    ),
    receivables: (
      <>
        <path d="M6 3.5h9l4 4V20a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1Z" />
        <path d="M14.5 3.8V8h4.2M8 12h8M8 16h8" />
        <path d="M8 8h2" />
      </>
    ),
    growth: (
      <>
        <path d="M4 19.5h16" />
        <path d="M6 16V11h3v5M11 16V7h3v9M16 16V4h3v12" />
        <path d="m5 8 5-4 4 2 5-4" />
        <path d="M16 2h3v3" />
      </>
    ),
  };

  return (
    <span className="cr-icon-mark" aria-hidden="true">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        {icons[name]}
      </svg>
    </span>
  );
}

type SystemName =
  | "sap"
  | "crm"
  | "dms"
  | "pos"
  | "pricing"
  | "promotions"
  | "invoices"
  | "receivables"
  | "external";

function SystemIcon({ name }: { name: SystemName }) {
  const iconShapes: Record<SystemName, React.ReactNode> = {
    sap: (
      <>
        <path d="M4 7.5 12 3l8 4.5-8 4.5-8-4.5Z" />
        <path d="M4 12 12 16.5 20 12M4 16.5 12 21l8-4.5" />
      </>
    ),
    crm: (
      <>
        <circle cx="9" cy="8" r="3" />
        <circle cx="17" cy="9" r="2.3" />
        <path d="M3.5 19v-1.2a5.5 5.5 0 0 1 11 0V19H3.5ZM15 14.5a4.3 4.3 0 0 1 5.5 4.1V19H17" />
      </>
    ),
    dms: (
      <>
        <path d="M6 3.5h8l4 4V20a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1Z" />
        <path d="M14 3.8V8h4M8 12h8M8 15.5h8M8 18.5h5" />
      </>
    ),
    pos: (
      <>
        <rect x="3.5" y="4" width="17" height="16" rx="2" />
        <path d="M7 8h10M7 11.5h10M9 16h6" />
      </>
    ),
    pricing: (
      <>
        <path d="M20.5 13 13 20.5a1.8 1.8 0 0 1-2.5 0L3.5 13.5a1.8 1.8 0 0 1 0-2.5L11 3.5h6.5l3 3V13Z" />
        <circle cx="15.2" cy="8.3" r="1.2" />
      </>
    ),
    promotions: (
      <>
        <path d="M3 10h18v10H3zM2 6h20v4H2zM12 6v14" />
        <path d="M12 6H8.5a2.2 2.2 0 1 1 2.2-2.2C10.7 5 12 6 12 6ZM12 6h3.5a2.2 2.2 0 1 0-2.2-2.2C13.3 5 12 6 12 6Z" />
      </>
    ),
    invoices: (
      <>
        <path d="M6 3.5h12a1 1 0 0 1 1 1V21l-3-1.8L12 21l-4-1.8L5 21V4.5a1 1 0 0 1 1-1Z" />
        <path d="M8.5 8h7M8.5 11.5h7M8.5 15h5" />
      </>
    ),
    receivables: (
      <>
        <rect x="3" y="5.5" width="18" height="14" rx="2" />
        <path d="M3.5 9.5h17M7 14.5h4M16.5 14.5h.1" />
        <path d="M7 3.5h10" />
      </>
    ),
    external: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M3.5 12h17M12 3c2.5 2.5 3.6 5.5 3.6 9s-1.1 6.5-3.6 9c-2.5-2.5-3.6-5.5-3.6-9S9.5 5.5 12 3Z" />
        <path d="m16.5 4.5 3 0 0 3" />
      </>
    ),
  };

  return (
    <svg
      className="cr-system-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {iconShapes[name]}
    </svg>
  );
}

function WaterfallChart() {
  const bars = [
    { label: "Revenue", value: "100", height: 112, type: "start" },
    { label: "Discounts", value: "-12", height: 17 },
    { label: "Schemes", value: "-8", height: 13 },
    { label: "Returns", value: "-5", height: 9 },
    { label: "Freight", value: "-7", height: 12 },
    { label: "Service Cost", value: "-6", height: 10 },
    { label: "Credit Cost", value: "-4", height: 7 },
    { label: "Product / Mix Effect", value: "-18", height: 28 },
    { label: "Contribution", value: "40", height: 45, type: "end" },
  ];

  return (
    <div
      className="cr-waterfall"
      role="img"
      aria-label="Illustrative waterfall chart showing revenue of 100 reduced by costs and deductions to contribution of 40"
    >
      <div className="cr-chart-axis">
        <span>120</span>
        <span>100</span>
        <span>80</span>
        <span>60</span>
        <span>40</span>
        <span>20</span>
        <span>0</span>
      </div>
      <div className="cr-chart-plot">
        <div className="cr-chart-gridlines">
          <i /><i /><i /><i /><i /><i /><i />
        </div>
        <div className="cr-bars">
          {bars.map((bar, i) => (
            <div
              className={`cr-bar-col ${bar.type || "deduction"}`}
              key={bar.label}
              data-reveal
              style={
                {
                  ...delay(i, 90, 800),
                  "--bar-height": `${bar.height}px`,
                } as React.CSSProperties
              }
            >
              <span className="cr-bar-value">{bar.value}</span>
              <div
                className="cr-bar"
                style={{ height: `${bar.height}px` }}
              />
              <span className="cr-bar-label">{bar.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Page ---------- */

export default function CustomerRevenuePage() {
  const ref = useScrollReveal();

  return (
    <main className="cr-page" ref={ref}>

      {/* HERO */}
      <section className="cr-hero">
        <div className="cr-hero-backdrop" aria-hidden="true">
          <img src={customerRevenueImages.hero} alt="" className="cr-hero-background-image" />
        </div>
        <div className="cr-wrap cr-hero-inner">
          <div className="cr-hero-copy">
            <h1 data-reveal style={delay(0)}>
              Revenue Is Not the
              <br />
              Same as Customer Value.
            </h1>
            <p data-reveal style={delay(1, 150)}>
              Understand which customers are growing, which are becoming
              less valuable, where margin is leaking, and where the next
              growth opportunity lies.
            </p>
            <div className="cr-hero-actions" data-reveal style={delay(2, 150)}>
              <a
                className="fp-buttons fp-btn-primary"
                href="/tellusyourproblem#tell-details"
              >
                Tell Us Your Customer &amp; Revenue Problem
              </a>
              <a
                className="fp-buttons fp-hero-outline-btn"
                href="#how-it-works"
              >
                See How It Works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* BUSINESS QUESTIONS */}
      <Section className="cr-questions">
        <Heading
          eyebrow="START WITH THE BUSINESS QUESTION"
          title="The questions commercial leaders ask"
          description="Real questions. Real decisions. Real impact."
        />
        <div className="cr-question-grid">
          {questions.map((question, index) => (
            <article
              className="cr-question"
              key={question}
              data-reveal
              style={delay(Math.floor(index / 2), 70)}
            >
              <b>{String(index + 1).padStart(2, "0")}</b>
              <span>{question}</span>
            </article>
          ))}
        </div>
      </Section>

      {/* CUSTOMER ECONOMICS */}
      <Section className="cr-economics" id="how-it-works">
        <Heading
          eyebrow="UNDERSTAND CUSTOMER ECONOMICS"
          title="Understand customer value across revenue, margin and cash"
          description="A complete view from revenue to cash."
        />
        <div className="cr-economics-diagram">
          <div className="cr-value-root" data-reveal>CUSTOMER VALUE</div>
          <div className="cr-branch-lines" aria-hidden="true">
            <i /><i /><i />
          </div>
          <div className="cr-economics-columns">
            <article className="cr-economics-column revenue" data-reveal style={delay(0, 120)}>
              <h3>REVENUE</h3>
              <ul>
                {[
                  "Volume",
                  "Price",
                  "Mix",
                  "Product",
                  "Channel",
                  "Geography",
                ].map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
            <article className="cr-economics-column margin" data-reveal style={delay(1, 120)}>
              <h3>MARGIN</h3>
              <ul>
                {[
                  "Product Margin",
                  "Channel Margin",
                  "Discount / Scheme",
                  "Cost-to-Serve",
                  "Customer Mix",
                  "Profitability",
                ].map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
            <article className="cr-economics-column cash" data-reveal style={delay(2, 120)}>
              <h3>CASH</h3>
              <ul>
                {[
                  "Receivable",
                  "Collection",
                  "Credit Terms",
                  "Overdue",
                  "Risk",
                  "Working Capital",
                ].map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          </div>
          <div className="cr-down-arrow" aria-hidden="true">↓</div>
          <div className="cr-true-profit" data-reveal>
            TRUE CUSTOMER PROFITABILITY
          </div>
        </div>
      </Section>

      {/* DIMENSIONS */}
      <Section className="cr-dimensions">
        <Heading
          eyebrow="SEE REVENUE THROUGH EVERY DIMENSION"
          title="See revenue through every dimension"
          description="Analyse, compare and act across multiple lenses."
        />
        <div className="cr-dimensions-layout">
          <div className="cr-dimension-list">
            {dimensions.map((dimension, index) => (
              <div key={dimension} data-reveal style={delay(index, 50)}>
                <span className="cr-dimension-count">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="cr-dimension-label">
                  {dimension}
                </span>
              </div>
            ))}
          </div>
          <div className="cr-dimensions-image" data-reveal style={delay(2, 100)}>
            <img
              src={customerRevenueImages.dimensions}
              alt="Revenue analysis across business dimensions"
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* INTELLIGENCE AREAS */}
      <Section className="cr-areas">
        <Heading
          eyebrow="CUSTOMER & REVENUE INTELLIGENCE AREAS"
          title="Customer & revenue intelligence areas"
          description="Focus on what matters. Solve real business problems."
        />
        <div className="cr-area-grid">
          {intelligenceAreas.map(([title, description, icon], index) => (
            <article
              className="cr-area-card cr-area-card--top-accent"
              key={title}
              data-reveal
              style={delay(index % 3, 100)}
            >
              <IconMark name={icon as IconName} />
              <h3>{title}</h3>
              <p>{description}</p>
            </article>
          ))}
        </div>
      </Section>

      {/* WORKED QUESTION */}
      <Section className="cr-worked">
        <Heading
          eyebrow="ILLUSTRATIVE WORKED QUESTION"
          title="Which customers generate revenue but destroy margin?"
          description="Revenue to true customer value · Illustrative view"
        />
        <div className="cr-worked-layout">
          <div className="cr-waterfall-panel" data-reveal>
            <div className="cr-panel-title">
              <strong>Revenue to True Customer Value</strong>
              <span>₹ Crore</span>
            </div>
            <WaterfallChart />
          </div>

          <div className="cr-customer-example" data-reveal style={delay(1, 150)}>
            <h3>
              Customer Example <small>(illustrative)</small>
            </h3>
            <div className="cr-customer-table">
              {[
                ["Revenue", "High (₹ 100 Cr)", ""],
                ["Growth", "+12%", "positive"],
                ["Margin", "Low (6%)", "negative"],
                ["Schemes", "High (18%)", "negative"],
                ["Receivable", "76 days", "negative"],
                ["Cost-to-Serve", "High", "negative"],
              ].map(([label, value, type]) => (
                <div key={label}>
                  <span>{label}</span>
                  <b className={type}>{value}</b>
                </div>
              ))}
            </div>
            <div className="cr-looks-actually">
              <div>
                <small>LOOKS LIKE</small>
                <b>A GOOD CUSTOMER</b>
              </div>
              <div>
                <small>ACTUALLY</small>
                <b>VALUE DILUTION</b>
              </div>
            </div>
            <div className="cr-decision-arrow">↓</div>
            <div className="cr-decision">
              <small>DECISION</small>
              <strong>
                Price / Mix / Scheme / Terms / Service
              </strong>
            </div>
          </div>
        </div>
      </Section>

      {/* INSIGHT TO ACTION */}
      <Section className="cr-action-section">
        <Heading
          eyebrow="FROM INSIGHT TO ACTION"
          title="From insight to action"
          description="A structured way to move from data to decisions."
        />
        <div className="cr-action-layout">
          <div className="cr-action-image" data-reveal>
            <img
              src={customerRevenueImages.insightToAction}
              alt="Revenue insights and business analytics"
              loading="lazy"
            />
          </div>
          <div className="cr-action-list">
            {actionSteps.map(([title, description], index) => (
              <article
                className="cr-action-row"
                key={title}
                data-reveal
                style={delay(index, 80, 500)}
              >
                <b>{String(index + 1).padStart(2, "0")}</b>
                <div>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </Section>

      {/* BUSINESS PROBLEMS */}
      <Section className="cr-problems">
        <Heading
          eyebrow="RELEVANT BUSINESS PROBLEMS"
          title="Relevant 52 business problems (15–26)"
          description="Key customer and revenue questions. Explore the full library."
        />
        <div className="cr-problems-layout">
          <div className="cr-problem-list" data-reveal>
            {connectedQuestions.map((question, index) => (
              <a href="/tellusyourproblem#tell-details" key={question}>
                <b>{index + 15}</b>
                <span>{question}</span>
                <i>→</i>
              </a>
            ))}
          </div>
          <div className="cr-connected" data-reveal style={delay(1, 150)}>
            <h3>Customer &amp; revenue questions:</h3>
            {businessProblems.map((problem, index) => (
              <a href="/tellusyourproblem#tell-details" key={problem}>
                <b>{index + 21}</b>
                <span>{problem}</span>
              </a>
            ))}
            <a className="cr-explore" href="/52problem#all-problems">
              Explore All 52 Business Problems <b>→</b>
            </a>
          </div>
        </div>
      </Section>

      {/* ENTERPRISE DATA */}
      <Section className="cr-enterprise-data">
        <Heading
          eyebrow="ENTERPRISE SYSTEMS & DATA"
          title="Enterprise systems & data"
          description="Bring data together. Add business context. Deliver intelligence."
        />
        <div className="cr-systems-grid">
          {systems.map(([icon, name], index) => (
            <div key={name} data-reveal style={delay(index % 9, 60)}>
              <b className="cr-system-icon-wrap">
                <SystemIcon name={icon as SystemName} />
              </b>
              <span>{name}</span>
            </div>
          ))}
        </div>
        <div className="cr-data-pipeline">
          <div className="cr-pipeline-title" data-reveal>
            TRUSTED CUSTOMER DATA
          </div>
          <span className="cr-pipeline-arrow">↓</span>
          <div className="cr-context-grid">
            {dataContext.map(([title, description], index) => (
              <div key={title} data-reveal style={delay(index, 90)}>
                <b>{title}</b>
                <small>{description}</small>
              </div>
            ))}
          </div>
          <span className="cr-pipeline-arrow">↓</span>
          <div className="cr-pipeline-final" data-reveal>
            CUSTOMER &amp; REVENUE INTELLIGENCE
          </div>
        </div>
      </Section>

      {/* FINAL CTA */}
      <section className="cr-cta" id="contact">
        <div className="cr-cta-visual" aria-hidden="true">
          <img
            src={customerRevenueImages.cta}
            alt=""
            loading="lazy"
          />
        </div>
        <div className="cr-wrap cr-cta-inner">
          <div className="cr-cta-content">
            <span className="cr-eyebrow" data-reveal>
              CUSTOMER &amp; REVENUE
            </span>
            <h2 data-reveal style={delay(1, 120)}>
              Don’t Just Ask Who Buys More.
              <br />
              Understand Who Creates Value.
            </h2>
            <p data-reveal style={delay(2, 120)}>
              Bring us a customer, revenue, pricing, scheme or receivables
              question. We’ll work backwards from the business outcome to
              the data, intelligence and action required.
            </p>
            <a
  className="fp-buttons fp-btn-primary"
  href="/tellusyourproblem#tell-details"
  data-reveal
  style={delay(3, 120)}
>
  Tell Us Your Customer &amp; Revenue Problem
</a>
          </div>
        </div>
      </section>
    </main>
  );
}